import type {
  Fare,
  FarePriceBreakdown,
  FareSliceDeparture,
  FareSliceOutbound,
  Prices,
} from "@b2bportal/air-shopping-api";
import {
  FlightRatingsEnum,
  getFareId,
  getFareSliceId,
  getFlights,
  getMulticityFlightsData,
  getTags,
} from "@b2bportal/core-flights";
import type { IFareData, IFlightFares } from "@overrides/types";
import { createSelector } from "@reduxjs/toolkit";

export const getFareData = createSelector(
  getFlights,
  getMulticityFlightsData,
  (_, fareArr: Array<Fare | FareSliceOutbound | FareSliceDeparture>) => ({
    fareArr,
  }),
  (
    flightRes: ReturnType<typeof getFlights>,
    multicityFlightsRes: ReturnType<typeof getMulticityFlightsData>,
    {
      fareArr,
    }: { fareArr: Array<Fare | FareSliceOutbound | FareSliceDeparture> }
  ): IFlightFares => {
    const { data: flights } = flightRes;
    if (flights == null && multicityFlightsRes == null) return {};

    return fareArr.reduce((acc, fare) => {
      const fareSliceId = getFareSliceId(fare);

      const fareSlice =
        flights?.fareSlices[fareSliceId] ??
        multicityFlightsRes?.fareSlices[fareSliceId];

      // Transform enhanced fares to standard to support RBC's 4-fare system
      const baseFareShelfValue = fareSlice.fareShelf.value;
      const fareShelfValue =
        baseFareShelfValue === FlightRatingsEnum.enhanced
          ? FlightRatingsEnum.standard
          : baseFareShelfValue;

      const fareClass = FlightRatingsEnum[fareShelfValue];
      const fareShelf = fareShelfValue;

      const existingFare: IFareData | undefined = acc[fareClass];

      let price: Prices | FarePriceBreakdown | undefined;

      if (fare.amount?.fiat.value != null) {
        price = fare.amount;
      } else if (fare.priceBreakdown != null) {
        price = fare.priceBreakdown;
      }

      if (!existingFare) {
        const tags = getTags(fareSlice?.tags);
        acc[fareClass] = {
          fareId: getFareId(fare),
          fareName: fareSlice?.fareBrandName ?? "",
          fareClass: fareClass,
          fareShelf,
          price,
          tags,
          numberOfFares: -1,
          segments: fareSlice.segments,
        };
      }

      // store the lowest price to be displayed in the UI
      if (
        "fiat" in price &&
        acc[fareClass].price.fiat.value > price.fiat.value
      ) {
        acc[fareClass].price = fare.amount;
        acc[fareClass].fareName = fareSlice?.fareBrandName ?? "";
      } else if (
        "taxesAndFees" in price &&
        acc[fareClass].price.taxesAndFees.fiat.value >
          price.taxesAndFees.fiat.value
      ) {
        acc[fareClass].price = price;
        acc[fareClass].fareName = fareSlice?.fareBrandName ?? "";
      }
      acc[fareClass].numberOfFares = acc[fareClass].numberOfFares + 1;

      return acc;
    }, {} as IFlightFares);
  }
);
